import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function FloatingButtons() {
  const [isOpen, setIsOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showLabel, setShowLabel] = useState(true); // label visible / hidden
  const navigate = useNavigate();

  // Label timing (ms)
  const LABEL_VISIBLE_MS = 4000;
  const LABEL_HIDDEN_MS = 3000;

  // Toggle label: visible for 4s, hidden for 3s, repeat. Paused while menu is open.
  useEffect(() => {
    if (isOpen) return;

    const delay = showLabel ? LABEL_VISIBLE_MS : LABEL_HIDDEN_MS;
    const timer = setTimeout(() => setShowLabel((prev) => !prev), delay);

    return () => clearTimeout(timer);
  }, [showLabel, isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleStartpersonality = () => {
    navigate('/personalitydevelopement');
    setShowPopup(false);
  };

  const handleStartInterview = () => {
    navigate('/mockquestion');
    setShowPopup(false);
  };

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }

        .floating-button {
          animation: float 3s ease-in-out infinite;
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(40px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .popup-card {
          animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>

      {/* Main Floating Action Button */}
      <button
        onClick={toggleMenu}
        className="floating-button"
        style={{
          position: 'fixed',
          left: '20px',
          bottom: '20px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          boxShadow: '0 10px 28px rgba(102, 126, 234, 0.5)',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.15)';
          e.currentTarget.style.boxShadow = '0 14px 36px rgba(102, 126, 234, 0.7)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0px)';
          e.currentTarget.style.boxShadow = '0 10px 28px rgba(102, 126, 234, 0.5)';
        }}
        title="Explore Features"
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <polygon points="12 7 15 12 12 17 9 12" fill="white" opacity="0.8" />
          <path d="M12 7v10M7 12h10" strokeWidth="1.5" />
        </svg>
      </button>

      {/* Label: shows for a few seconds, hides, repeats (fades in/out) */}
      {!isOpen && (
        <div
          style={{
            position: 'fixed',
            left: '20px',
            bottom: '92px',
            padding: '8px 14px',
            borderRadius: '10px',
            background: '#ffffff',
            color: '#1a1a2e',
            fontSize: '12.5px',
            fontWeight: 700,
            letterSpacing: '0.1px',
            whiteSpace: 'nowrap',
            zIndex: 10000,
            boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
            pointerEvents: 'none',
            opacity: showLabel ? 1 : 0,
            transform: showLabel ? 'translateY(0)' : 'translateY(6px)',
            transition: 'opacity 0.4s ease, transform 0.4s ease',
          }}
        >
          Mock Interview &amp; Personality Development
          <div
            style={{
              position: 'absolute',
              bottom: '-5px',
              left: '22px',
              width: '10px',
              height: '10px',
              background: '#ffffff',
              transform: 'rotate(45deg)',
            }}
          />
        </div>
      )}

      {/* Personality Development Button */}
      <button
        onClick={() => {
          handleStartpersonality();
          setIsOpen(false);
        }}
        style={{
          position: 'fixed',
          left: '20px',
          bottom: isOpen ? '200px' : '20px',
          width: '260px',
          height: '60px',
          borderRadius: '30px',
          background: 'linear-gradient(135deg, #F093FB 0%, #F5576C 100%)',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          zIndex: 9999,
          boxShadow: '0 8px 20px rgba(245, 87, 108, 0.4)',
          transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transform: isOpen ? 'scale(1)' : 'scale(0.3)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = isOpen ? 'scale(1.08)' : 'scale(0.3)';
          e.currentTarget.style.boxShadow = '0 12px 28px rgba(245, 87, 108, 0.6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = isOpen ? 'scale(1)' : 'scale(0.3)';
          e.currentTarget.style.boxShadow = '0 8px 20px rgba(245, 87, 108, 0.4)';
        }}
        title="Personality Development"
      >
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0 }}
        >
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="5" r="1" />
          <circle cx="5" cy="19" r="1" />
          <circle cx="5" cy="5" r="1" />
          <circle cx="19" cy="19" r="1" />
          <path d="M12 3v2m0 12v2M3 12h2m12 0h2" strokeLinecap="round" />
        </svg>
        <span style={{ fontSize: '14px', fontWeight: '600', color: 'white', letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>
          Personality Development
        </span>
      </button>

      {/* Mock Interview Button */}
      <button
        onClick={() => {
          setIsOpen(false);
          setShowPopup(true);
        }}
        style={{
          position: 'fixed',
          left: '20px',
          bottom: isOpen ? '120px' : '20px',
          width: '260px',
          height: '60px',
          borderRadius: '30px',
          background: 'linear-gradient(135deg, #9548CC 0%, #7B3FF2 100%)',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          zIndex: 9999,
          boxShadow: '0 8px 20px rgba(149, 72, 204, 0.4)',
          transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transform: isOpen ? 'scale(1)' : 'scale(0.3)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = isOpen ? 'scale(1.08)' : 'scale(0.3)';
          e.currentTarget.style.boxShadow = '0 12px 28px rgba(149, 72, 204, 0.6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = isOpen ? 'scale(1)' : 'scale(0.3)';
          e.currentTarget.style.boxShadow = '0 8px 20px rgba(149, 72, 204, 0.4)';
        }}
        title="Mock Interview"
      >
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0 }}
        >
          <polygon points="23 7 16 12 23 17 23 7"></polygon>
          <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
        </svg>
        <span style={{ fontSize: '14px', fontWeight: '600', color: 'white', letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>
          Mock Interview
        </span>
      </button>

      {/* Overlay to close menu */}
      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9998,
          }}
        />
      )}

      {/* ============ MOCK INTERVIEW POPUP ============ */}
      {showPopup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 20000,
            backdropFilter: 'blur(5px)',
          }}
          onClick={() => setShowPopup(false)}
        >
          {/* Modal Card */}
          <div
            className="popup-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f5f0ff 100%)',
              borderRadius: '16px',
              padding: '32px 28px',
              maxWidth: '380px',
              width: '90%',
              boxShadow: '0 20px 40px rgba(149, 72, 204, 0.15)',
              position: 'relative',
              border: '1px solid rgba(149, 72, 204, 0.1)',
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowPopup(false)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                border: 'none',
                background: 'rgba(0, 0, 0, 0.06)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.06)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            {/* Icon */}
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #9548CC 0%, #7B3FF2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '18px',
                boxShadow: '0 10px 24px rgba(149, 72, 204, 0.25)',
              }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="23 7 16 12 23 17 23 7"></polygon>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
              </svg>
            </div>

            {/* Title */}
            <h1
              style={{
                fontSize: '22px',
                fontWeight: '700',
                color: '#1a1a2e',
                margin: '0 0 10px 0',
                lineHeight: '1.3',
              }}
            >
              Welcome to AI Mock Interview
            </h1>

            {/* Description */}
            <p
              style={{
                fontSize: '14px',
                color: '#666',
                margin: '0 0 22px 0',
                lineHeight: '1.5',
                fontWeight: '500',
              }}
            >
              Improve your interview skills with an AI interviewer.
            </p>

            {/* Details Grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                marginBottom: '22px',
              }}
            >
              {/* Duration */}
              <div
                style={{
                  background: 'rgba(149, 72, 204, 0.08)',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid rgba(149, 72, 204, 0.12)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <div style={{ fontSize: '10px', color: '#9548CC', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  Duration
                </div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#1a1a2e', marginTop: '4px' }}>
                  15-30 mins
                </div>
              </div>
            </div>

            {/* Start Interview Button */}
            <button
              onClick={handleStartInterview}
              style={{
                width: '100%',
                padding: '14px 20px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #9548CC 0%, #7B3FF2 100%)',
                border: 'none',
                fontSize: '15px',
                fontWeight: '600',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 8px 20px rgba(149, 72, 204, 0.3)',
                letterSpacing: '0.4px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 28px rgba(149, 72, 204, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(149, 72, 204, 0.3)';
              }}
            >
              Start Interview
            </button>
          </div>
        </div>
      )}
    </div>
  );
}